/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useState } from 'react'
import {
  DownloadOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { t } from '@sportninja/common/i18n'
import {
  ROSTER_TRANSFER_KINDS,
  parseRosterTransferKind,
  rosterExportFileName,
  rosterExportUrl,
  rosterTransferLabelKey,
  type RosterTransferKind,
} from '@sportninja/common/features/competition/operations/utils/rosterTransfer'
import Modal from 'src/components/Modal'
import ImportTeamRosterContent from 'src/components/ImportCSV/ImportTeamRoster/ImportTeamRosterContent'
import { SkeletonText } from 'src/components/ts-components/SkeletonBlock'
import { useFileDownload } from 'src/hooks/useFileDownload'
import { rosterImportTemplate } from '../../utils/rosterImportTemplate'
import PanelMenuTrigger from './PanelMenuTrigger'
import { panelIconSkeletonStyle } from './panelIconButtonStyle'

const translate = t as (key: string, options?: unknown) => string

interface RosterTransferActionsProps {
  teamId: string
  teamName: string
  /** Null until the roster request resolves; every endpoint is roster-scoped. */
  rosterId: string | null
  /** Import Players is allowed — the Team screen's `shouldAllowAddImportRoster`. */
  canImport: boolean
  /** Import Team Officials is allowed — the Team screen's `shouldAllowAddTeamOfficials`. */
  canImportOfficials: boolean
  importDisabledReason: string | null
  canExport: boolean
  /** True while the roster (and therefore the permissions) are still loading. */
  isLoading: boolean
  /** Refetches the roster after a successful import. */
  onImported: () => void
}

/**
 * Export and Import for a team's roster, as the Team screen's roster tab has
 * them: each an icon-only trigger opening a menu with the same two entries —
 * Players and Team Officials. Each import entry carries its own gate, exactly
 * as the Team screen disables them independently.
 *
 * The import modal is `ImportTeamRosterContent` — the Team screen's own,
 * mounted directly rather than through `ImportTeamRoster`, whose hard-coded
 * legacy trigger is shared with the Team and Org screens. Only the trigger and
 * the open/Escape state live here; the import itself (dropzone, template,
 * duplicate review, help link) is the shared component untouched.
 */
const RosterTransferActions = ({
  teamId,
  teamName,
  rosterId,
  canImport,
  canImportOfficials,
  importDisabledReason,
  canExport,
  isLoading,
  onImported,
}: RosterTransferActionsProps) => {
  'use memo'

  const [importKind, setImportKind] = useState<RosterTransferKind | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const { download, loading: isExporting } = useFileDownload()

  const closeImport = useCallback(() => setImportKind(null), [])

  // Matches the wrapper's own Escape handling, which the modal does not provide.
  useEffect(() => {
    if (!importKind) {
      return undefined
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setImportKind(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [importKind])

  // While loading, every permission reads false — indistinguishable from
  // "denied". A skeleton reserves the footprint so the header does not shift
  // when the real answer lands, matching the Add button beside it.
  if (isLoading) {
    return (
      <span
        css={panelIconSkeletonStyle}
        data-testid='roster-transfer-skeleton'
        aria-hidden
      >
        <SkeletonText width={34} height={34} />
        <SkeletonText width={34} height={34} />
      </span>
    )
  }

  const canImportKind: Record<RosterTransferKind, boolean> = {
    players: canImport,
    teamOfficials: canImportOfficials,
  }
  const canImportAny = canImport || canImportOfficials
  // Shown while denied too, so the tooltip can say why.
  const showImport = (canImportAny || Boolean(importDisabledReason)) && rosterId

  return (
    <>
      {canExport && rosterId && (
        <PanelMenuTrigger
          label={translate('common:exportRoster')}
          icon={isExporting ? <LoadingOutlined /> : <DownloadOutlined />}
          disabled={isExporting}
          menu={{
            items: ROSTER_TRANSFER_KINDS.map((kind) => ({
              key: kind,
              label: translate(rosterTransferLabelKey('export', kind)),
            })),
            onClick: ({ key }) => {
              const kind = parseRosterTransferKind(key)
              if (kind) {
                download({
                  url: rosterExportUrl(teamId, rosterId, kind),
                  fileName: rosterExportFileName(teamName, kind),
                })
              }
            },
          }}
        />
      )}

      {showImport && (
        <PanelMenuTrigger
          label={translate('common:importRoster')}
          icon={<UploadOutlined />}
          disabled={!canImportAny || isImporting}
          tooltip={
            canImportAny
              ? translate('common:importRoster')
              : importDisabledReason
          }
          menu={{
            items: ROSTER_TRANSFER_KINDS.map((kind) => ({
              key: kind,
              label: translate(rosterTransferLabelKey('import', kind)),
              disabled: !canImportKind[kind],
            })),
            onClick: ({ key }) => setImportKind(parseRosterTransferKind(key)),
          }}
        />
      )}

      <Modal
        isOpen={Boolean(importKind)}
        onClose={closeImport}
        shouldCloseOnOverlayClick={false}
      >
        {importKind && rosterId && (
          <ImportTeamRosterContent
            loading={isImporting}
            setLoading={setIsImporting}
            toggle={closeImport}
            importTemplate={rosterImportTemplate(importKind)}
            teamId={teamId}
            teamName={teamName}
            teamRosterId={rosterId}
            rosterType={importKind}
            onSuccess={onImported}
          />
        )}
      </Modal>
    </>
  )
}

export default RosterTransferActions
